import React, { useEffect, useState } from 'react';
import requiredIf from 'react-required-if';
import { usePrevious } from 'react-use';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { isEqual, isNil, random } from 'lodash';

import { useStyles } from '~/assets/styles';
import { useClaim } from '~/components/ClaimContainer';
import { getGlobalImageProxyBaseUrl } from '~/components/CmsMain/globals';
import CommunicationAttachmentTable from '~/components/communications/CommunicationAttachmentTable';
import CommunicationReminderContainer from '~/components/communications/CommunicationCard/CommunicationReminderContainer';
import CommunicationRecipientContainerFormik from '~/components/communications/CommunicationRecipientContainer';
import EditorFormik from '~/components/communications/EmailCommunicationCard/EmailEditor/EditorFormik';
import ContactsValidEmailWarning from '~/components/communications/EmailCommunicationCard/WarningEmail';
import { getAttachmentFileUrl } from '~/components/communications/utils';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import List from '~/components/core/Atomic/List';
import ListItem from '~/components/core/Atomic/ListItem';
import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Typography from '~/components/core/Atomic/Typography';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { createAttachedDocumentEntry } from '~/components/Documents/DocumentsAttachment';
import { ESIGN_SIGNATURE_URL_PLACEHOLDER } from '~/components/Documents/ESign/utils';
import FormikDocumentsAttachment from '~/components/Documents/FormikDocumentsAttachment';
import UploadDocumentFromTemplate from '~/components/Documents/UploadDocumentFromTemplate';
import FailedTokensWarningBanner from '~/components/GenericTemplates/FailedTokensWarningBanner';
import GenericTemplateSelectionContainerFormik from '~/components/GenericTemplates/FromTemplate/GenericTemplateSelectionContainerFormik';
import SecureIframe from '~/components/SecureIframe/SecureIframe';
import TextFieldFormik from '~/components/TextFieldFormik';
import Divider from '~/components/ui/atoms/Divider';
import { reportErrorInProductionOrThrow } from '~/Utils';

import cn from '../../../Utils/cn';

import EmailSpoofingVerificationAlertBanner from './Banners/EmailSpoofingVerificationAlertBanner';
import { useEmailGeneralConfiguration } from './hooks/useEmailGeneralConfiguration';
import AttachmentForwardingSection from './AttachmentForwardingSection';
import EmailRecipient from './EmailRecipient';
import { recipientContactValidationScheme } from './schemaValidations';
import SupervisorSenderSelection from './SupervisorSenderSelection';
import useEmailSignatureHtml from './useEmailSignatureHtml';

export const EmailCommunicationSpecificBody = (props) => {
  const { classes } = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { claim, onClaimUpdate } = useClaim();

  // forcedRecipient is given in case of reply to email not attached to a claim
  const {
    communication,
    communicationInitialFields,
    isDocument,
    isNew,
    onUpdate,
    forcedRecipient,
    isNotClaimRelated,
    emailSignature,
    initialBody,
    shouldOverrideTitleWithTemplate = true,
    shouldContactInitialBodyWithTemplate = false,
    shouldDisplaySupervisorSenderSelection = false,
    showTranslation,
    initialExposureId,
    initialDocumentTemplateId,
    initialGenericTemplateId,
    parentAttachments,
    referredCommunicationId,
    initialFailedTokens,
    moiId,
    moiExposureId,
  } = props;
  const [editorKey, setEditorKey] = useState('init');
  const [isFromTemplateDialogOpen, setIsFromTemplateDialogOpen] = useState(false);
  // Seeded by pre-filled flows (e.g. the MOI assignment email) whose server-side render already
  // collected unresolved tokens; the in-dialog template picker overwrites it on later selections.
  const [failedTokens, setFailedTokens] = useState(initialFailedTokens ?? null);
  const [attachedDocumentTemplateId, setAttachedDocumentTemplateId] = useState(null);
  const [attachedDocumentTemplateExposureId, setAttachedDocumentTemplateExposureId] = useState(null);
  const [requiresESignature, setRequiresESignature] = useState(false);
  const [hasInitializedDocumentDialog, setHasInitializedDocumentDialog] = useState(false);
  const [hasInitializedGenericTemplateDialog, setHasInitializedGenericTemplateDialog] = useState(false);
  const [signatureUrlInsertError, setSignatureUrlInsertError] = useState(null);
  const titleFieldId = 'subject';
  const bodyFieldId = 'body_html';

  const [emailSignatureHtml, setEmailSignatureHtml] = useState(emailSignature);
  const [currentSendAsUserId, setCurrentSendAsUserId] = useState(values.send_as_user_id);
  const [shouldFetchSignature, setShouldFetchSignature] = useState(false);
  const [pendingNewUserId, setPendingNewUserId] = useState(null);

  const {
    data: emailConfigHtml,
    isLoading,
    isError,
  } = useEmailSignatureHtml({
    claimId: claim?.id,
    referredCommunicationId,
    sendAsUserId: pendingNewUserId || values.send_as_user_id,
    shouldFetch: shouldFetchSignature,
  });

  const { generalConfiguration } = useEmailGeneralConfiguration();
  const supervisorSendAsOtherAdjusterEnabled = generalConfiguration?.supervisor_send_as_other_adjuster ?? false;

  const prevSignatureRef = usePrevious(emailConfigHtml);
  const bodyHtmlField = values[bodyFieldId];

  // Handle initial signature setup on component mount
  useEffect(() => {
    if (!shouldDisplaySupervisorSenderSelection || initialBody === undefined || bodyHtmlField !== '') {
      return;
    }
    // Seed the body even when the organization has no signature configured: this effect is the only
    // writer of body_html on this path, so bailing out opens the composer with no quoted block at all.
    const nextBody = emailSignature ? `<br>${emailSignature}<br>${initialBody}` : initialBody;
    if (!nextBody) {
      return;
    }
    setFieldValue(bodyFieldId, nextBody);
    if (emailSignature) {
      setEmailSignatureHtml(emailSignature);
    }
    setEditorKey(`sig-${random(1000000)}`);
  }, [emailSignature, initialBody, setFieldValue, bodyFieldId, bodyHtmlField, shouldDisplaySupervisorSenderSelection]);

  // Handle signature changes when sender is switched
  useEffect(() => {
    if (
      emailConfigHtml &&
      !isEqual(prevSignatureRef, emailConfigHtml) &&
      !isLoading &&
      !isError &&
      shouldFetchSignature &&
      pendingNewUserId
    ) {
      // Update formik value
      setFieldValue('send_as_user_id', pendingNewUserId);
      setCurrentSendAsUserId(pendingNewUserId);

      // Prepend signature to initialBody instead of replacing entire body
      if (emailConfigHtml?.user_email_signature_html) {
        const emailBody = `<br>${emailConfigHtml.user_email_signature_html}<br>${initialBody}`;
        setFieldValue(bodyFieldId, emailBody);
        setEmailSignatureHtml(emailConfigHtml.user_email_signature_html);
        setEditorKey(`sig-${random(1000000)}`);
      }

      // Reset fetch state
      setShouldFetchSignature(false);
      setPendingNewUserId(null);
    }
  }, [
    emailConfigHtml,
    isLoading,
    isError,
    shouldFetchSignature,
    pendingNewUserId,
    setFieldValue,
    bodyFieldId,
    prevSignatureRef,
    initialBody,
  ]);

  // Handler that triggers signature fetch after confirmation
  const handleSendAsUserChange = (newUserId) => {
    setPendingNewUserId(newUserId);
    setShouldFetchSignature(true);
  };

  // Handler for the confirmation modal - this will be called by WithConfirm
  const handleSendAsUserChangeWithConfirm = (newUserId) => {
    if (newUserId === currentSendAsUserId) return;

    // This method will be called after the user confirms
    handleSendAsUserChange(newUserId);
  };

  // The EditorFormik wraps Editor components for content updates. Due to Editors' tight coupling
  // with their initial content, direct updates don't trigger rerenders. By updating the component's key via useEffect,
  // we force a rerender to reflect content changes, ensuring the UI stays updated
  useEffect(() => {
    if (!values.template_id) {
      return;
    }

    setEditorKey(values.template_id);
  }, [values.template_id]);

  const handleOpenFromTemplateDialog = () => {
    setIsFromTemplateDialogOpen(true);
    setSignatureUrlInsertError(null);
  };

  const initiateUploadDocumentFromTemplateDialog = (documentTemplateId, exposureId, requiresESignature = false) => {
    setAttachedDocumentTemplateId(documentTemplateId);
    setAttachedDocumentTemplateExposureId(exposureId);
    setRequiresESignature(requiresESignature);
    setSignatureUrlInsertError(null);
  };

  useEffect(() => {
    if (!hasInitializedDocumentDialog && initialDocumentTemplateId && typeof initialExposureId === 'number') {
      initiateUploadDocumentFromTemplateDialog(initialDocumentTemplateId, initialExposureId);
      setHasInitializedDocumentDialog(true);
    }
  }, [initialDocumentTemplateId, initialExposureId, hasInitializedDocumentDialog]);

  useEffect(() => {
    if (!hasInitializedGenericTemplateDialog && initialGenericTemplateId) {
      setIsFromTemplateDialogOpen(true);
      setHasInitializedGenericTemplateDialog(true);
    }
  }, [initialGenericTemplateId, hasInitializedGenericTemplateDialog]);

  const handleCloseFromTemplateDialog = (handleCloseFromTemplateDialogParams) => {
    setIsFromTemplateDialogOpen(false);
    const exposureId =
      handleCloseFromTemplateDialogParams &&
      !isNil(handleCloseFromTemplateDialogParams.documentTemplateExposureId) &&
      !isNaN(handleCloseFromTemplateDialogParams.documentTemplateExposureId)
        ? handleCloseFromTemplateDialogParams.documentTemplateExposureId
        : 0;
    initiateUploadDocumentFromTemplateDialog(
      handleCloseFromTemplateDialogParams?.documentTemplateId,
      exposureId,
      handleCloseFromTemplateDialogParams?.requiresESignature ?? false
    );
  };

  const isView = !isNew && !isDocument;
  const maxAttachmentsHeight = '200px';

  let documentsAttachmentComponent;
  if (isView) {
    documentsAttachmentComponent = communication.attachments.length > 0 && (
      <div className="pb-40">
        <CommunicationAttachmentTable
          communication={communication}
          communicationType="emails"
          onUpdate={onUpdate}
          maxHeight={maxAttachmentsHeight}
          getAttachmentFileUrl={(attachment) => getAttachmentFileUrl(attachment, 'emails')}
        />
      </div>
    );
  } else {
    documentsAttachmentComponent = isNotClaimRelated ? undefined : (
      <FormikDocumentsAttachment id="attached_documents" maxHeight={maxAttachmentsHeight} />
    );
  }

  let recipientAddressComponent;
  if (!isView) {
    if (forcedRecipient) {
      recipientAddressComponent = (
        <div className={classes.container} style={{ justifyContent: 'center', alignItems: 'center' }}>
          <Typography display="block" variant="subtitle2">
            {forcedRecipient.emailAddress}
          </Typography>
        </div>
      );
    } else {
      // The normal flow

      if (
        communicationInitialFields.communicationContact &&
        communicationInitialFields.communicationContact.emails.length > 0
      ) {
        if (communicationInitialFields.communicationContact.emails.length === 1) {
          recipientAddressComponent = (
            <div className={cn(classes.container)}>
              <Typography display="block" variant="subtitle2">
                {communicationInitialFields.communicationContact.emails[0].email}
              </Typography>
            </div>
          );
        } else {
          recipientAddressComponent = (
            <div className="w-1/2 max-w-[280px]">
              <TextFieldFormik id="email_id" label="Email" className={classes.textField} fullWidth select>
                {communicationInitialFields.communicationContact.emails.map((email) => (
                  <MenuItem key={email.id} value={email.id}>
                    {email.email}
                  </MenuItem>
                ))}
              </TextFieldFormik>
            </div>
          );
        }
      }
    }
  }

  const defaultDeliveryErrorMsg =
    "Unknown reason. Please try double-checking the recipient's email address, or try again later";

  const formattedContactName =
    communication?.contact?.full_name && communication?.contact_email?.email
      ? `${communication.contact.full_name} (${communication.contact_email.email})`
      : communication?.specific_identifier;
  const recipientTypes = isView ? ['to', 'cc', 'bcc'] : ['cc', 'bcc'];

  const handleCloseUploadDocumentPreview = () => {
    setAttachedDocumentTemplateId(null);
    setAttachedDocumentTemplateExposureId(null);
  };

  const handleDocumentAutoAttach = (documentId, updatedClaim) => {
    handleCloseUploadDocumentPreview();
    if (!isNil(documentId) && !isNaN(documentId)) {
      let newAttachedDocuments = [...(values['attached_documents'] || [])];
      if (!newAttachedDocuments.map((doc) => doc.id).includes(documentId)) {
        newAttachedDocuments = [...newAttachedDocuments, createAttachedDocumentEntry(updatedClaim, documentId)];
        setFieldValue('attached_documents', newAttachedDocuments);
      }
    }
  };

  const insertSignatureUrlIntoBody = (signUrl) => {
    try {
      const currentBody = values[bodyFieldId] || '';
      const eSignatureUrlHtml = `<a href="${signUrl}" target="_blank">Signature Link</a>`;
      if (currentBody.includes(ESIGN_SIGNATURE_URL_PLACEHOLDER)) {
        const newBody = currentBody.replaceAll(ESIGN_SIGNATURE_URL_PLACEHOLDER, eSignatureUrlHtml);
        setFieldValue(bodyFieldId, newBody);
      } else {
        setFieldValue(bodyFieldId, `${eSignatureUrlHtml}<br/><br/>${currentBody}`);
      }

      setEditorKey(`sig-url-${random(1000000)}`);
      setSignatureUrlInsertError(null);
    } catch (error) {
      handleCloseUploadDocumentPreview();
      setSignatureUrlInsertError(signUrl);
    }
  };

  return (
    <>
      {recipientAddressComponent}
      {!isView && supervisorSendAsOtherAdjusterEnabled && shouldDisplaySupervisorSenderSelection ? (
        <SupervisorSenderSelection onChange={handleSendAsUserChangeWithConfirm} />
      ) : null}
      {isView && (
        <List dense className="empty:hidden">
          {communication.delivery_issue === 'pending' && (
            <ListItem>
              <ListItemText
                primary={formattedContactName && `Email delivery pending for ${formattedContactName}`}
                primaryTypographyProps={{ style: { color: 'orange' } }}
              />
            </ListItem>
          )}
          {communication.delivery_issue === 'error' && (
            <ListItem>
              <ListItemText
                primary={formattedContactName && `Email delivery failed for ${formattedContactName}`}
                primaryTypographyProps={{ color: 'error' }}
                secondary={communication.delivery_issue_reason || defaultDeliveryErrorMsg}
                secondaryTypographyProps={{ color: 'textSecondary', variant: 'caption' }}
              />
            </ListItem>
          )}
          {communication.direction === 'Incoming' && (
            <EmailSpoofingVerificationAlertBanner communication={communication} />
          )}
          {[
            ...(communication?.to_contacts ?? []),
            ...(communication?.cc_contacts ?? []),
            ...(communication?.bcc_contacts ?? []),
          ].map((recipientEntry) => {
            if (recipientEntry.delivery_issue === 'pending') {
              return (
                <ListItem key={recipientEntry.email}>
                  <ListItemText
                    primary={`Email delivery pending for ${recipientEntry.contact_full_name} (${recipientEntry.email})`}
                    primaryTypographyProps={{ style: { color: 'orange' } }}
                  />
                </ListItem>
              );
            } else if (recipientEntry.delivery_issue === 'error') {
              return (
                <ListItem key={recipientEntry.email}>
                  <ListItemText
                    primary={`Email delivery failed for ${recipientEntry.contact_full_name} (${recipientEntry.email})`}
                    primaryTypographyProps={{ color: 'error' }}
                    secondary={recipientEntry.delivery_issue_reason || defaultDeliveryErrorMsg}
                    secondaryTypographyProps={{ color: 'textSecondary', variant: 'caption' }}
                  />
                </ListItem>
              );
            } else {
              return null;
            }
          })}
        </List>
      )}
      {recipientTypes.map((recipientType) => (
        <div className={cn(classes.inputContainer, 'empty:hidden')} key={recipientType}>
          <CommunicationRecipientContainerFormik
            isView={isView}
            AdditionalComponent={ContactsValidEmailWarning}
            SpecificRecipientDialog={EmailRecipient}
            recipientContactValidationScheme={recipientContactValidationScheme}
            recipientType={recipientType}
            isNotClaimRelated={isNotClaimRelated}
          />
        </div>
      ))}
      {!isNotClaimRelated && !isView ? (
        <div className="flex items-center justify-end">
          <Button color="primary" onClick={handleOpenFromTemplateDialog}>
            <AddIcon />
            From template
          </Button>
        </div>
      ) : null}
      <FailedTokensWarningBanner className="pb-20" failedTokens={failedTokens} />
      {signatureUrlInsertError ? (
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.ERROR}
          title="Failed to insert signature link"
          note={
            <>
              The document was uploaded successfully, but we couldn&apos;t insert the signing link into the email. You
              can copy the link here:{' '}
              <a href={signatureUrlInsertError} target="_blank" rel="noopener noreferrer">
                {signatureUrlInsertError}
              </a>
              <br />
              If the link is not visible, you can find it on the Documents page by clicking the document status on the
              document that was just created from the template.
            </>
          }
        />
      ) : null}
      <div className={classes.inputContainer}>
        <TextFieldFormik id="subject" label="Subject:" className={classes.textField} fullWidth showOnly={isView} />
      </div>
      <Divider />
      <div className="my-4 flex-grow">
        {isView && !(showTranslation ? communication.translated_body_html : communication.body_html) ? (
          <TextFieldFormik
            id="body_text"
            label="Body:"
            className={classes.textField}
            fullWidth
            multiline
            rows={10}
            showOnly
          />
        ) : isView ? (
          <SecureIframe
            html={showTranslation ? communication.translated_body_html : communication.body_html}
            className="h-full w-full border-none"
            imageProxyOrigin={getGlobalImageProxyBaseUrl()}
            // Same-origin inline-attachment endpoint the read-time cid: rewriter
            // targets — path-scoped so only attachment endpoints load as images.
            attachmentOriginUrl={`${window.location.origin}/api/v1/communications/`}
          />
        ) : (
          <EditorFormik
            id={showTranslation ? 'translated_body_html' : 'body_html'}
            label="Body:"
            className={classes.textField}
            classNameEditor="max-h-[24vh] overflow-auto"
            fullWidth
            maxHeight={isView ? null : '45vh'}
            showOnly={isView}
            autoFocus
            key={editorKey}
            displayConfig={{
              font: true,
              textColor: true,
              textStyle: true,
              align: true,
              link: true,
            }}
          />
        )}
      </div>
      <Divider />
      {parentAttachments && parentAttachments.length > 0 && !isView && (
        <AttachmentForwardingSection attachments={parentAttachments} />
      )}
      <div className={classes.inputContainer}>{documentsAttachmentComponent}</div>
      {isNew && !isNotClaimRelated && <CommunicationReminderContainer />}
      {isFromTemplateDialogOpen ? (
        <GenericTemplateSelectionContainerFormik
          handleClose={handleCloseFromTemplateDialog}
          templateType="email"
          titleFieldId={titleFieldId}
          bodyFieldId={bodyFieldId}
          signature={emailSignatureHtml}
          setFailedTokens={setFailedTokens}
          shouldOverrideTitle={shouldOverrideTitleWithTemplate}
          shouldContactInitialBody={shouldContactInitialBodyWithTemplate}
          initialBody={initialBody}
          initialGenericTemplateId={initialGenericTemplateId}
          initialExposureId={initialExposureId}
          moiId={moiId}
          moiExposureId={moiExposureId}
        />
      ) : null}
      {!isNil(attachedDocumentTemplateId) ? (
        <UploadDocumentFromTemplate
          claim={claim}
          onCancel={handleCloseUploadDocumentPreview}
          onClose={handleCloseUploadDocumentPreview}
          onSubmitDocument={async (documentId, signUrl) => {
            const updatedClaim = await onClaimUpdate();
            if (signUrl) {
              // E-signature flow: insert signing link, don't attach document
              insertSignatureUrlIntoBody(signUrl);
              handleCloseUploadDocumentPreview();
            } else if (updatedClaim) {
              handleDocumentAutoAttach(documentId, updatedClaim);
            } else {
              reportErrorInProductionOrThrow(
                `Failed to update claim in select email template with document attachement in EmailCommunicationSpecificBody, after uploading document id: ${documentId}`
              );
            }
          }}
          open
          disableMinimized
          withInvolvedInfo={false}
          initialValueExposureId={attachedDocumentTemplateExposureId}
          initialValueTemplateId={attachedDocumentTemplateId}
          requiresESignature={requiresESignature}
        />
      ) : null}
    </>
  );
};

EmailCommunicationSpecificBody.propTypes = {
  communication: requiredIf(PropTypes.object, (props) => !props.isNew && !props.isDocument),
  isNew: PropTypes.bool,
  isDocument: PropTypes.bool,
  communicationInitialFields: PropTypes.shape({
    communicationChannel: PropTypes.string.isRequired,
    communicationContact: PropTypes.object,
    communicationDirection: requiredIf(PropTypes.string, (props) => props.isNew),
  }),
  onUpdate: requiredIf(PropTypes.func, (props) => !props.isNew && !props.isDocument),
  forcedRecipient: requiredIf(
    PropTypes.shape({
      optionalContact: PropTypes.object,
      optionalContactEmail: PropTypes.object,
      emailAddress: PropTypes.string,
    }),
    (props) => !!props.isNotClaimRelated
  ),
  isNotClaimRelated: PropTypes.bool,
  emailSignature: PropTypes.string,
  initialBody: PropTypes.string,
  shouldOverrideTitleWithTemplate: PropTypes.bool,
  shouldContactInitialBodyWithTemplate: PropTypes.string,
  shouldDisplaySupervisorSenderSelection: PropTypes.bool,
  showTranslation: PropTypes.bool,
  initialExposureId: PropTypes.number,
  initialDocumentTemplateId: PropTypes.number,
  initialGenericTemplateId: PropTypes.number,
  parentAttachments: PropTypes.array,
  referredCommunicationId: PropTypes.string,
  initialFailedTokens: PropTypes.arrayOf(PropTypes.string),
  moiId: PropTypes.number,
  moiExposureId: PropTypes.number,
};

export default EmailCommunicationSpecificBody;
